/**
 * 🎯 AnnouncementItem Component
 * 
 * Hərrac detalları səhifəsi - SignalR Context ilə real-time yeniləmələr.
 * 
 * Features:
 * - Real-time bid updates via SignalR Context
 * - Auction extension notifications
 * - User outbid alerts
 * - Winner announcement
 * - Deposit management
 * - Report system
 * 
 * @version 3.0.0
 */

import React, { useContext, useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { HerracContext } from '../../App';
import { useSignalR } from '../services/SignalRContext';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DynamicProductDetails from '../top/categories/dynamicProductDetails/DynamicProductDetails';
import Loading from '../Loading';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';

// Services & Hooks
import DepositService, {
    calculateDeposit,
    calculateMinBidIncrement,
    calculateNextMinBid,
    formatCurrency,
    validateBid,
    validateBalance,
    DEPOSIT_CONFIG
} from '../services/DepositService';
import { useAuctionTimer } from '../services/UseAuctionTimer';
import BalanceService from '../services/BalanceService';

import {
    FaGavel, FaClock, FaUser, FaPhone, FaWhatsapp, FaTrophy,
    FaCheckCircle, FaExclamationTriangle, FaChevronLeft, FaChevronRight,
    FaExpand, FaTimes, FaHistory, FaChartLine, FaCalendarAlt,
    FaMapMarkerAlt, FaTag, FaImage, FaWallet, FaInfoCircle, FaCoins, FaWifi
} from 'react-icons/fa';

const API_BASE_URL = 'https://api.herracim.az';
const IMAGE_BASE_URL = `${API_BASE_URL}/HerracImages`;
const MAX_EXTEND_DAYS = 10;

function AnnouncementItem() {
    const { user, balance, getUserBalance } = useContext(HerracContext);
    const { id } = useParams();
    const navigate = useNavigate();

    // ============================================
    // SIGNALR CONTEXT
    // ============================================
    const { 
        joinAuction, 
        leaveAuction, 
        onBidUpdate, 
        onAuctionEnded, 
        onAuctionExtended,
        onUserOutbid,
        onAuctionWon,
        onBalanceUpdated,
        isAuctionConnected 
    } = useSignalR();

    // Refs - dövrə düşməni önləmək üçün
    const hasInitialized = useRef(false);

    // Core States
    const [isAdmin, setIsAdmin] = useState(false);
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Image States
    const [activeIndex, setActiveIndex] = useState(0);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [imageLoadError, setImageLoadError] = useState({});

    // Bid States
    const [highestBid, setHighestBid] = useState(null);
    const [bidHistory, setBidHistory] = useState([]);
    const [confirmBidModal, setConfirmBidModal] = useState(false);
    const [bidToConfirm, setBidToConfirm] = useState(null);
    const [isSubmittingBid, setIsSubmittingBid] = useState(false);

    // Deposit States
    const [depositInfo, setDepositInfo] = useState(null);
    const [hasUserDeposit, setHasUserDeposit] = useState(false);
    const [confirmDepositModal, setConfirmDepositModal] = useState(false);
    const [confirmingDeposit, setConfirmingDeposit] = useState(false);
    const [depositAlreadyConfirmed, setDepositAlreadyConfirmed] = useState(false);
    const [isCheckingStatus, setIsCheckingStatus] = useState(true);
    const [isWinner, setIsWinner] = useState(false);
    const [showDepositInfo, setShowDepositInfo] = useState(false);

    // Seller Info State
    const [sellerInfo, setSellerInfo] = useState(null);

    // Report States
    const [showReportModal, setShowReportModal] = useState(false);
    const [reportMessage, setReportMessage] = useState("");
    const [isReporting, setIsReporting] = useState(false);
    const [hasReported, setHasReported] = useState(false);

    // Extend Auction States
    const [newEndDate, setNewEndDate] = useState('');
    const [extendingAuction, setExtendingAuction] = useState(false);
    const [hasCheckedAfterEnd, setHasCheckedAfterEnd] = useState(false);

    // Timer Hook
    const timerEndDate = product?.endDate || null;
    const { 
        days, hours, minutes, seconds, 
        isEnded: timerIsEnded, 
        isLoading: timerIsLoading,
        isWarning: isTimerWarning, 
        isUrgent: isTimerUrgent 
    } = useAuctionTimer(timerEndDate, {
        onEnd: () => {
            if (product?.id) {
                toast.info('⏰ Hərrac başa çatdı!');
            }
        },
        onWarning: () => toast.warning('⚠️ Hərrac bitməsinə 5 dəqiqə qaldı!')
    });

    const auctionEnded = !timerIsLoading && timerIsEnded;

    // ============================================
    // MEMOIZED VALUES
    // ============================================
    const getImageUrl = useCallback((imageName) => {
        if (!imageName) return '/images/placeholder.jpg';
        if (imageName.startsWith('http')) return imageName;
        const cleanImageName = imageName.startsWith('/') ? imageName.slice(1) : imageName;
        return `${IMAGE_BASE_URL}/${cleanImageName}`;
    }, []);

    const activeImageUrl = useMemo(() => {
        if (!product?.images || product.images.length === 0) return '/images/placeholder.jpg';
        if (imageLoadError[activeIndex]) return '/images/placeholder.jpg';
        return getImageUrl(product.images[activeIndex]);
    }, [product?.images, activeIndex, imageLoadError, getImageUrl]);

    const currentPrice = useMemo(() => highestBid?.amount || product?.price || 0, [highestBid?.amount, product?.price]);
    const nextBidAmount = useMemo(() => calculateNextMinBid(currentPrice), [currentPrice]);
    const minBidIncrement = useMemo(() => calculateMinBidIncrement(currentPrice), [currentPrice]);
    const requiredDeposit = useMemo(() => calculateDeposit(product?.price || 0), [product?.price]);
    const isHighestBidder = useMemo(() => user?.userId === highestBid?.userId, [user?.userId, highestBid?.userId]);
    
    const isSeller = useMemo(() => {
        if (!user?.userId || !product) return false;
        const productUserId = product?.userId || product?.user?.userId;
        return user.userId === productUserId;
    }, [user?.userId, product]);

    const canExtendAuction = useMemo(() => {
        return isSeller && auctionEnded && bidHistory.length === 0;
    }, [isSeller, auctionEnded, bidHistory.length]);

    const canShowConfirmDeposit = useMemo(() => {
        return auctionEnded && highestBid && user && isWinner && !depositAlreadyConfirmed && !hasReported && !isCheckingStatus;
    }, [auctionEnded, highestBid, user, isWinner, depositAlreadyConfirmed, hasReported, isCheckingStatus]);

    // ============================================
    // UTILITY FUNCTIONS
    // ============================================
    const handleImageError = useCallback((index) => {
        setImageLoadError(prev => ({ ...prev, [index]: true }));
    }, []);
    
    const maskFullName = useCallback((fullName) => {
        if (!fullName) return '';
        return fullName.split(' ').map(part => part[0] + '*'.repeat(part.length - 1)).join(' ');
    }, []);

    const formatPhoneNumber = useCallback((number) => {
        if (!number) return '';
        const cleaned = number.replace(/\D/g, '');
        if (cleaned.length === 12) return `+${cleaned.slice(0, 3)} (${cleaned.slice(3, 5)}) ${cleaned.slice(5, 8)}-${cleaned.slice(8, 10)}-${cleaned.slice(10)}`;
        if (cleaned.length === 10) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 5)}-${cleaned.slice(5, 7)}-${cleaned.slice(7)}`;
        return number;
    }, []);

    // ============================================
    // IMAGE FUNCTIONS
    // ============================================
    const changeActiveImage = useCallback((index) => {
        if (!product?.images || product.images.length === 0) return;
        setActiveIndex(index);
    }, [product?.images]);

    const openImageModal = useCallback((index) => {
        if (imageLoadError[index]) return;
        setActiveIndex(index);
        setIsModalOpen(true);
    }, [imageLoadError]);

    const closeImageModal = useCallback(() => setIsModalOpen(false), []);
    
    const changeImage = useCallback((direction) => {
        if (!product?.images || product.images.length === 0) return;
        setActiveIndex(prevIndex => (prevIndex + direction + product.images.length) % product.images.length);
    }, [product?.images]);

    // ============================================
    // BID FUNCTIONS
    // ============================================
    const submitBid = useCallback(async (amount) => {
        if (auctionEnded) { toast.error("⏰ Hərrac bitmişdir."); return false; }
        if (!user) { toast.error("🔒 Giriş etməlisiniz!"); navigate('/giris'); return false; }
        if (isSeller) { toast.error("❌ Siz öz elanınıza təklif verə bilməzsiniz."); return false; }

        const bidAmount = parseFloat(amount);
        const bidValidation = validateBid(bidAmount, currentPrice);
        if (!bidValidation.isValid) { toast.error(`💰 ${bidValidation.message}`); return false; }

        if (!hasUserDeposit) {
            const balanceValidation = validateBalance(balance, product?.price, hasUserDeposit);
            if (!balanceValidation.isValid) { toast.error(`💳 ${balanceValidation.message}`); return false; }
        }

        setIsSubmittingBid(true);
        try {
            const result = await DepositService.placeBid(user.userId, product.id, bidAmount);
            if (result.success) {
                setHighestBid({ amount: bidAmount, productId: product.id, userName: user.fullName, userId: user.userId });
                if (result.data.isFirstBid) {
                    await getUserBalance(user.userId);
                    setHasUserDeposit(true);
                    toast.success(`✅ Təklif göndərildi! Depozit tutuldu: ${formatCurrency(result.data.depositAmount)}`);
                } else {
                    toast.success(`✅ Təklif göndərildi: ${formatCurrency(bidAmount)}`);
                }
                return true;
            } else {
                toast.error(`❌ ${result.error}`);
                return false;
            }
        } catch (error) {
            toast.error("❌ Təklif göndərilə bilmədi.");
            return false;
        } finally {
            setIsSubmittingBid(false);
        }
    }, [auctionEnded, user, isSeller, currentPrice, hasUserDeposit, balance, product, getUserBalance, navigate]);

    const handleBidClick = useCallback(() => {
        if (!user) { toast.error("🔒 Giriş etməlisiniz!"); navigate('/giris'); return; }
        setBidToConfirm(nextBidAmount);
        setConfirmBidModal(true);
    }, [nextBidAmount, user, navigate]);

    const confirmAndSubmitBid = useCallback(async () => {
        if (!bidToConfirm) { toast.error("Təklif məbləği boşdur!"); return; }
        const success = await submitBid(bidToConfirm);
        if (success) setConfirmBidModal(false);
    }, [bidToConfirm, submitBid]);

    // ============================================
    // DEPOSIT FUNCTIONS
    // ============================================
    const handleConfirmDeposit = useCallback(async () => {
        setConfirmingDeposit(true);
        try {
            const result = await BalanceService.confirmDeposit(product.id);
            if (result.success) {
                toast.success(`✅ Depozit köçürüldü! Satıcıya: ${formatCurrency(result.data.sellerGets)}, Xidmət haqqı: ${formatCurrency(result.data.platformFee)}`);
                setDepositAlreadyConfirmed(true);
            } else {
                toast.error(`❌ ${result.error}`);
            }
        } catch (err) {
            toast.error("❌ Xəta baş verdi.");
        } finally {
            setConfirmingDeposit(false);
            setConfirmDepositModal(false);
        }
    }, [product?.id]);

    // ============================================
    // REPORT FUNCTIONS
    // ============================================
    const submitReport = useCallback(async () => {
        if (!reportMessage.trim()) { toast.error("⚠️ Şikayət mətni boş ola bilməz!"); return; }
        setIsReporting(true);
        try {
            const result = await BalanceService.submitReport(product.id, reportMessage);
            if (result.success) {
                toast.success("✅ Şikayətiniz göndərildi.");
                setHasReported(true);
                setShowReportModal(false);
                setReportMessage("");
            } else {
                toast.error(`❌ ${result.error}`);
            }
        } catch (err) {
            toast.error("❌ Xəta baş verdi.");
        } finally {
            setIsReporting(false);
        }
    }, [reportMessage, product?.id]);

    // ============================================
    // EXTEND AUCTION
    // ============================================
    const handleExtendAuction = useCallback(async () => {
        if (!newEndDate) { toast.error("⚠️ Yeni tarix seçin."); return; }
        if (!isSeller || !auctionEnded || bidHistory.length > 0) { toast.error("❌ Hərrac uzadıla bilməz."); return; }

        const selectedDate = dayjs(newEndDate);
        const now = dayjs();
        if (selectedDate.isBefore(now.add(1, 'hour'))) { toast.error("❌ Ən azı 1 saat sonra olmalıdır."); return; }
        if (selectedDate.isAfter(now.add(MAX_EXTEND_DAYS, 'days'))) { toast.error(`❌ Maksimum ${MAX_EXTEND_DAYS} gün.`); return; }

        setExtendingAuction(true);
        try {
            const dateString = selectedDate.toISOString();
            await axios.post(`${API_BASE_URL}/api/ProductBase/${product.id}/ExtendAuction`, JSON.stringify(dateString), { 
                headers: { "Content-Type": "application/json" }, 
                withCredentials: true 
            });
            toast.success("✅ Vaxt uzadıldı!");
            setProduct(prev => ({ ...prev, endDate: dateString }));
            setNewEndDate('');
            setHasCheckedAfterEnd(false);
        } catch (error) {
            toast.error(`❌ ${error.response?.data?.message || "Xəta baş verdi."}`);
        } finally {
            setExtendingAuction(false);
        }
    }, [newEndDate, product?.id, isSeller, auctionEnded, bidHistory.length]);

    // ============================================
    // DATA FETCHING FUNCTIONS
    // ============================================
    const fetchBidData = useCallback(async (productId) => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/bid/${productId}`);
            if (res.data && res.data.amount > 0) {
                setHighestBid({ 
                    amount: res.data.amount, 
                    productId: res.data.productId, 
                    userId: res.data.userId, 
                    userName: res.data.userName, 
                    phoneNumber: res.data.phoneNumber 
                });
            }
        } catch (err) { 
            console.error("Bid data error:", err); 
        }
    }, []);

    const fetchBidHistory = useCallback(async (productId) => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/Bid/history/${productId}`);
            setBidHistory(res.data || []);
        } catch (err) { 
            console.error("History error:", err); 
        }
    }, []);

    const fetchDepositInfo = useCallback(async (productId) => {
        try {
            const result = await BalanceService.getDepositInfo(productId);
            if (result.success) setDepositInfo(result.data);
        } catch (err) { 
            console.error("Deposit info error:", err); 
        }
    }, []);

    const fetchSellerInfo = useCallback(async (productId) => {
        try {
            const res = await axios.get(`${API_BASE_URL}/api/ProductBase/${productId}/seller`, { 
                withCredentials: true 
            });
            if (res.data) {
                setSellerInfo(res.data);
            }
        } catch (err) { 
            console.error("Seller info error:", err);
            if (product?.user) {
                setSellerInfo({
                    fullName: product.user.fullName,
                    phoneNumber: product.user.phoneNumber
                });
            }
        }
    }, [product?.user]);

    const checkAllStatuses = useCallback(async (userId, productId) => {
        if (!userId || !productId) return;
        setIsCheckingStatus(true);
        try {
            const [winnerResult, confirmedResult, reportResult, depositResult] = await Promise.all([
                BalanceService.isWinner(productId),
                BalanceService.hasConfirmed(productId),
                BalanceService.hasReported(productId),
                BalanceService.hasDeposit(productId)
            ]);
            setIsWinner(winnerResult?.isWinner || false);
            setDepositAlreadyConfirmed(confirmedResult?.confirmed || false);
            setHasReported(reportResult?.hasReported || false);
            setHasUserDeposit(depositResult?.hasDeposit || false);
        } catch (err) { 
            console.error("Status check error:", err); 
        } finally { 
            setIsCheckingStatus(false); 
        }
    }, []);

    // ============================================
    // INITIAL EFFECTS
    // ============================================
    
    // Navigation Check
    useEffect(() => {
        if (!id) navigate('/error');
    }, [id, navigate]);

    // Admin Check
    useEffect(() => {
        setIsAdmin(user?.roles?.includes("Admin") || false);
    }, [user?.roles]);

    // Product Fetch
    useEffect(() => {
        if (!id || hasInitialized.current) return;
        
        const fetchProduct = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/ProductBase/${id}`);
                let productData = res.data.product || res.data.products?.[0] || res.data;
                
                if (productData) {
                    setProduct(productData);
                    hasInitialized.current = true;
                    
                    await Promise.all([
                        fetchBidData(id),
                        fetchBidHistory(id),
                        fetchDepositInfo(id)
                    ]);
                } else {
                    setError("Məhsul tapılmadı");
                }
            } catch (err) { 
                setError("Məhsul yüklənmədi"); 
                console.error("Product fetch error:", err);
            } finally { 
                setLoading(false); 
            }
        };
        
        fetchProduct();
    }, [id, fetchBidData, fetchBidHistory, fetchDepositInfo]);

    // Image index reset
    useEffect(() => {
        if (product?.images?.length > 0) {
            setActiveIndex(0);
        }
    }, [product?.images]);

    // User status check
    useEffect(() => {
        if (user?.userId && product?.id) {
            checkAllStatuses(user.userId, product.id);
        }
    }, [user?.userId, product?.id, checkAllStatuses]);

    // Fetch seller info when winner
    useEffect(() => {
        if (isWinner && auctionEnded && product?.id && !sellerInfo) {
            fetchSellerInfo(product.id);
        }
    }, [isWinner, auctionEnded, product?.id, sellerInfo, fetchSellerInfo]);

    // ============================================
    // SIGNALR - JOIN AUCTION ROOM
    // ============================================
    useEffect(() => {
        if (!id) return;

        // SignalR Context ilə hərrac otağına qoşul
        joinAuction(id);
        console.log(`🎯 SignalR: Hərrac #${id} otağına qoşuldu`);

        return () => {
            leaveAuction(id);
            console.log(`👋 SignalR: Hərrac #${id} otağından ayrıldı`);
        };
    }, [id, joinAuction, leaveAuction]);

    // ============================================
    // SIGNALR - BID UPDATES
    // ============================================
    useEffect(() => {
        const unsubscribe = onBidUpdate((data) => {
            // Bu hərrac üçün yoxla
            const dataProductId = String(data.auctionId || data.productId);
            if (dataProductId !== String(id)) return;

            console.log('📈 SignalR: Bid yeniləndi', data);

            const newAmount = data.currentBid || data.amount;
            const bidderId = data.bidderId || data.userId || data.oderId;
            const bidderName = data.bidderName || data.userName;

            setHighestBid(prev => {
                if (!prev || newAmount > prev.amount) {
                    return { 
                        amount: newAmount, 
                        productId: parseInt(id), 
                        userId: bidderId, 
                        userName: bidderName 
                    };
                }
                return prev;
            });

            // Bid history-ə əlavə et
            setBidHistory(prev => [{
                oderId: bidderId,
                userName: bidderName,
                amount: newAmount,
                bidTime: data.timestamp || new Date().toISOString()
            }, ...prev].slice(0, 50));

            // Başqa birisi təklif verdikdə toast göstər
            if (bidderId !== user?.userId) {
                toast.success(`🎉 Yeni təklif: ${formatCurrency(newAmount)} - ${bidderName}`, { autoClose: 3000 });
            }
        });

        return () => unsubscribe();
    }, [id, user?.userId, onBidUpdate]);

    // ============================================
    // SIGNALR - USER OUTBID
    // ============================================
    useEffect(() => {
        if (!user) return;

        const unsubscribe = onUserOutbid((data) => {
            const dataProductId = String(data.auctionId || data.productId);
            if (dataProductId !== String(id)) return;

            console.log('😤 SignalR: Təklif keçildi', data);
            toast.warning(`😤 Təklifiniz keçildi! Yeni təklif: ${formatCurrency(data.newBidAmount || data.amount)}`);
        });

        return () => unsubscribe();
    }, [id, user, onUserOutbid]);

    // ============================================
    // SIGNALR - AUCTION ENDED
    // ============================================
    useEffect(() => {
        const unsubscribe = onAuctionEnded((data) => {
            const dataProductId = String(data.auctionId || data.productId);
            if (dataProductId !== String(id)) return;

            console.log('🏁 SignalR: Hərrac bitdi', data);
            
            if (data.winnerId) {
                setHighestBid(prev => ({
                    ...prev,
                    userId: data.winnerId,
                    userName: data.winnerName,
                    amount: data.finalBid || data.winningBid || prev?.amount
                }));

                if (data.winnerId === user?.userId) {
                    setIsWinner(true);
                    toast.success('🏆 Təbriklər! Hərracı qazandınız!');
                }
            }

            toast.info('⏰ Hərrac başa çatdı!');
        });

        return () => unsubscribe();
    }, [id, user?.userId, onAuctionEnded]);

    // ============================================
    // SIGNALR - AUCTION EXTENDED
    // ============================================
    useEffect(() => {
        const unsubscribe = onAuctionExtended((data) => {
            const dataProductId = String(data.auctionId || data.productId);
            if (dataProductId !== String(id)) return;

            console.log('⏰ SignalR: Hərrac uzadıldı', data);
            
            setProduct(prev => ({ 
                ...prev, 
                endDate: data.newEndDate 
            }));
            
            setHasCheckedAfterEnd(false);
            toast.info(`⏰ Hərrac ${data.extensionMinutes || 2} dəqiqə uzadıldı!`);
        });

        return () => unsubscribe();
    }, [id, onAuctionExtended]);

    // ============================================
    // SIGNALR - AUCTION WON
    // ============================================
    useEffect(() => {
        if (!user) return;

        const unsubscribe = onAuctionWon((data) => {
            const dataProductId = String(data.auctionId || data.productId);
            if (dataProductId !== String(id)) return;

            if (data.winnerId === user.userId) {
                console.log('🏆 SignalR: Hərracı qazandınız', data);
                setIsWinner(true);
                toast.success('🏆 Təbriklər! Hərracı qazandınız!');
                
                // Satıcı məlumatlarını yüklə
                fetchSellerInfo(id);
            }
        });

        return () => unsubscribe();
    }, [id, user, onAuctionWon, fetchSellerInfo]);

    // ============================================
    // SIGNALR - BALANCE UPDATES
    // ============================================
    useEffect(() => {
        if (!user) return;

        const unsubscribe = onBalanceUpdated((data) => {
            if (data.userId === user.userId) {
                console.log('💰 SignalR: Balans yeniləndi', data);
                getUserBalance(user.userId);
            }
        });

        return () => unsubscribe();
    }, [user, getUserBalance, onBalanceUpdated]);

    // ============================================
    // AUCTION END EFFECT - REFUND
    // ============================================
    useEffect(() => {
        if (auctionEnded && product?.id && !hasCheckedAfterEnd && highestBid?.userId) {
            axios.post(`${API_BASE_URL}/api/BidDeposit/RefundDeposits`, null, { 
                params: { productId: product.id, winnerUserId: highestBid.userId } 
            })
                .then(() => {
                    toast.success(`🎉 Qalib: ${highestBid.userName}`);
                    if (user?.userId) {
                        checkAllStatuses(user.userId, product.id);
                    }
                })
                .catch(err => console.error("Refund error:", err));
            
            setHasCheckedAfterEnd(true);
        }
    }, [auctionEnded, product?.id, highestBid, hasCheckedAfterEnd, user?.userId, checkAllStatuses]);

    // Modal scroll lock
    useEffect(() => {
        const hasOpenModal = confirmDepositModal || isModalOpen || confirmBidModal || showReportModal || showDepositInfo;
        document.body.style.overflow = hasOpenModal ? 'hidden' : 'auto';
        return () => { document.body.style.overflow = 'auto'; };
    }, [confirmDepositModal, isModalOpen, confirmBidModal, showReportModal, showDepositInfo]);

    // ============================================
    // RENDER
    // ============================================
    if (loading) return <Loading />;
    
    if (error || !product) {
        return (
            <div className="auctiondetail-error">
                <FaExclamationTriangle className="auctiondetail-error__icon" />
                <h2>{error || "Məhsul tapılmadı"}</h2>
                <button className="auctiondetail-error__btn" onClick={() => navigate('/elanlar')}>Geri qayıt</button>
            </div>
        );
    }

    const hasImages = product.images && product.images.length > 0;
    const sellerPhone = sellerInfo?.phoneNumber || product?.user?.phoneNumber || product?.phoneNumber;
    const sellerName = sellerInfo?.fullName || product?.user?.fullName || "Satıcı";

    return (
        <div className="auctiondetail-page">
            {/* Connection Status Indicator */}
            <div className={`auctiondetail-connection ${isAuctionConnected ? 'connected' : 'disconnected'}`}>
                <FaWifi />
                <span>{isAuctionConnected ? 'Real-time aktiv' : 'Bağlantı yoxdur'}</span>
            </div>

            <div className="auctiondetail-container">
                {/* Gallery */}
                <div className="auctiondetail-gallery">
                    <div className="auctiondetail-gallery__main">
                        {hasImages && (
                            <button 
                                className="auctiondetail-gallery__nav auctiondetail-gallery__nav--prev" 
                                onClick={() => changeImage(-1)} 
                                disabled={product.images.length <= 1}
                            >
                                <FaChevronLeft />
                            </button>
                        )}
                        {!imageLoadError[activeIndex] ? (
                            <img 
                                src={activeImageUrl} 
                                alt={product.name} 
                                onClick={() => openImageModal(activeIndex)} 
                                className="auctiondetail-gallery__mainimg" 
                                onError={() => handleImageError(activeIndex)} 
                                loading="lazy" 
                            />
                        ) : (
                            <div className="auctiondetail-gallery__placeholder">
                                <FaImage />
                                <p>Şəkil yüklənmir</p>
                            </div>
                        )}
                        {hasImages && (
                            <>
                                <button 
                                    className="auctiondetail-gallery__nav auctiondetail-gallery__nav--next" 
                                    onClick={() => changeImage(1)} 
                                    disabled={product.images.length <= 1}
                                >
                                    <FaChevronRight />
                                </button>
                                <button 
                                    className="auctiondetail-gallery__expand" 
                                    onClick={() => openImageModal(activeIndex)} 
                                    disabled={imageLoadError[activeIndex]}
                                >
                                    <FaExpand />
                                </button>
                            </>
                        )}
                    </div>
                    <div className="auctiondetail-gallery__thumbs">
                        {hasImages ? product.images.map((img, i) => (
                            <div 
                                key={i} 
                                className={`auctiondetail-gallery__thumb ${activeIndex === i ? 'auctiondetail-gallery__thumb--active' : ''}`} 
                                onClick={() => !imageLoadError[i] && changeActiveImage(i)}
                            >
                                {!imageLoadError[i] ? (
                                    <img 
                                        src={getImageUrl(img)} 
                                        alt={`Thumb ${i + 1}`} 
                                        onError={() => handleImageError(i)} 
                                        loading="lazy" 
                                    />
                                ) : (
                                    <div className="auctiondetail-gallery__thumb-error">
                                        <FaImage />
                                    </div>
                                )}
                            </div>
                        )) : (
                            <div className="auctiondetail-gallery__noimages">
                                <FaImage />
                                <p>Şəkil yoxdur</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Info */}
                <div className="auctiondetail-info">
                    <div className="auctiondetail-info__header">
                        <h1 className="auctiondetail-info__title">{product.name}</h1>
                        <div className="auctiondetail-info__cattag">
                            <FaTag />
                            <span>{product.categoryName}{product.subCategoryName && ` / ${product.subCategoryName}`}</span>
                        </div>
                    </div>

                    {/* Status */}
                    <div className="auctiondetail-status">
                        <div className="auctiondetail-status__head">
                            <FaGavel />
                            <h2>Hərrac Statusu</h2>
                            {isAuctionConnected && (
                                <span className="auctiondetail-status__live">
                                    <FaWifi /> CANLI
                                </span>
                            )}
                        </div>
                        
                        {/* Timer */}
                        <div className={`auctiondetail-timer ${auctionEnded ? 'auctiondetail-timer--ended' : ''} ${isTimerWarning ? 'auctiondetail-timer--warning' : ''} ${isTimerUrgent ? 'auctiondetail-timer--urgent' : ''}`}>
                            {timerIsLoading ? (
                                <div className="auctiondetail-timer__loading">
                                    <FaClock />
                                    <span>Yüklənir...</span>
                                </div>
                            ) : auctionEnded ? (
                                <div className="auctiondetail-timer__finished">
                                    <FaClock />
                                    <span>Hərrac başa çatıb</span>
                                </div>
                            ) : (
                                <>
                                    <div className="auctiondetail-timer__label">
                                        <FaClock />
                                        <span>Qalan vaxt</span>
                                    </div>
                                    <div className="auctiondetail-timer__display">
                                        {days > 0 && (
                                            <div className="auctiondetail-timer__unit">
                                                <span className="auctiondetail-timer__value">{days}</span>
                                                <span className="auctiondetail-timer__text">Gün</span>
                                            </div>
                                        )}
                                        <div className="auctiondetail-timer__unit">
                                            <span className="auctiondetail-timer__value">{hours}</span>
                                            <span className="auctiondetail-timer__text">Saat</span>
                                        </div>
                                        <div className="auctiondetail-timer__unit">
                                            <span className="auctiondetail-timer__value">{minutes}</span>
                                            <span className="auctiondetail-timer__text">Dəq</span>
                                        </div>
                                        <div className="auctiondetail-timer__unit">
                                            <span className="auctiondetail-timer__value">{seconds}</span>
                                            <span className="auctiondetail-timer__text">San</span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Pricing */}
                        <div className="auctiondetail-pricing">
                            <div className="auctiondetail-pricing__row">
                                <span>Başlanğıc qiymət:</span>
                                <span>{formatCurrency(product.price)}</span>
                            </div>
                            <div className="auctiondetail-pricing__row auctiondetail-pricing__row--featured">
                                <span><FaTrophy /> Ən yüksək təklif:</span>
                                <span className="auctiondetail-pricing__value--current">
                                    {highestBid?.amount ? formatCurrency(highestBid.amount) : "Təklif yoxdur"}
                                </span>
                            </div>
                            {highestBid && (
                                <div className="auctiondetail-pricing__bidder">
                                    <img src="/images/profile.svg" alt="User" />
                                    <span>
                                        {isSeller || isHighestBidder ? highestBid.userName : maskFullName(highestBid.userName)}
                                        {isHighestBidder && <strong> (Siz)</strong>}
                                    </span>
                                </div>
                            )}
                        </div>

                        {/* Deposit Info */}
                        {!isSeller && !auctionEnded && (
                            <div className="auctiondetail-deposit-info">
                                <div 
                                    className="auctiondetail-deposit-info__header" 
                                    onClick={() => setShowDepositInfo(true)}
                                >
                                    <FaCoins />
                                    <span>Depozit Məlumatı</span>
                                    <FaInfoCircle />
                                </div>
                                <div className="auctiondetail-deposit-info__summary">
                                    <div className="auctiondetail-deposit-info__item">
                                        <span>Tələb olunan:</span>
                                        <strong>{formatCurrency(requiredDeposit)}</strong>
                                    </div>
                                    <div className="auctiondetail-deposit-info__item">
                                        <span>Min artırım:</span>
                                        <strong>+{formatCurrency(minBidIncrement)}</strong>
                                    </div>
                                    {hasUserDeposit && (
                                        <div className="auctiondetail-deposit-info__badge">
                                            <FaCheckCircle /> Depozit ödənilib
                                        </div>
                                    )}
                                </div>
                                {user && !hasUserDeposit && (
                                    <div className="auctiondetail-deposit-info__balance">
                                        <FaWallet />
                                        <span>Balans: {formatCurrency(balance)}</span>
                                        {balance < requiredDeposit && (
                                            <Link to="/kabinetim/balans" className="auctiondetail-deposit-info__topup">
                                                Artır
                                            </Link>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Dates */}
                        <div className="auctiondetail-dates">
                            <div className="auctiondetail-dates__item">
                                <FaCalendarAlt />
                                <span>Başlama:</span>
                                <strong>{new Date(product.createdAt).toLocaleString("az-AZ")}</strong>
                            </div>
                            <div className="auctiondetail-dates__item">
                                <FaCalendarAlt />
                                <span>Bitmə:</span>
                                <strong>{new Date(product.endDate).toLocaleString("az-AZ")}</strong>
                            </div>
                            {product.city && (
                                <div className="auctiondetail-dates__item">
                                    <FaMapMarkerAlt />
                                    <span>Şəhər:</span>
                                    <strong>{product.city}</strong>
                                </div>
                            )}
                        </div>

                        {/* Bid Button */}
                        {!isSeller && (
                            <div className="auctiondetail-bidbox">
                                <button 
                                    className={`auctiondetail-bidbox__btn ${auctionEnded ? 'auctiondetail-bidbox__btn--disabled' : ''}`} 
                                    disabled={auctionEnded || isSubmittingBid || timerIsLoading} 
                                    onClick={handleBidClick}
                                >
                                    <FaGavel />
                                    <span>
                                        {timerIsLoading ? "Yüklənir..." : auctionEnded ? "Hərrac bitib" : isSubmittingBid ? "Göndərilir..." : `Təklif et: ${formatCurrency(nextBidAmount)}`}
                                    </span>
                                </button>
                                {!auctionEnded && !hasUserDeposit && !timerIsLoading && (
                                    <p className="auctiondetail-bidbox__note">
                                        İlk təklif üçün {formatCurrency(requiredDeposit)} depozit tutulacaq
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Bid History */}
                    <div className="auctiondetail-history">
                        <div className="auctiondetail-history__head">
                            <FaHistory />
                            <h3>Tarixçə</h3>
                            <span className="auctiondetail-history__count">{bidHistory.length}</span>
                        </div>
                        {bidHistory.length > 0 ? (
                            <div className="auctiondetail-history__list">
                                {bidHistory.map((bid, i) => (
                                    <div key={i} className="auctiondetail-history__item">
                                        <div className="auctiondetail-history__user">
                                            <img src="/images/profile.svg" alt="User" />
                                            <span>
                                                {user?.userId === bid.userId || user?.userId === bid.oderId ? (
                                                    <strong>{bid.userName} (Siz)</strong>
                                                ) : (
                                                    maskFullName(bid.userName)
                                                )}
                                            </span>
                                        </div>
                                        <div className="auctiondetail-history__amount">{formatCurrency(bid.amount)}</div>
                                        <div className="auctiondetail-history__time">{new Date(bid.bidTime).toLocaleString("az-AZ")}</div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="auctiondetail-history__empty">
                                <FaChartLine />
                                <p>Hələ təklif yoxdur</p>
                            </div>
                        )}
                    </div>

                    {/* Contact Info */}
                    <div className="auctiondetail-elaqe">
                        <div className="auctiondetail-elaqe__head">
                            <FaUser />
                            <h3>Əlaqə</h3>
                        </div>
                        <div className="auctiondetail-elaqe__content">
                            {isSeller ? (
                                !auctionEnded ? (
                                    <div className="auctiondetail-elaqe__seller-msg">
                                        <p>🚀 Məhsulunuz satışdadır.</p>
                                    </div>
                                ) : highestBid ? (
                                    <div className="auctiondetail-elaqe__winner">
                                        <p>🎉 <strong>Hərrac bitdi!</strong></p>
                                        <div className="auctiondetail-elaqe__details">
                                            <div className="auctiondetail-elaqe__row">
                                                <FaUser />
                                                <span>Qalib: {highestBid.userName}</span>
                                            </div>
                                            {highestBid.phoneNumber && (
                                                <>
                                                    <div className="auctiondetail-elaqe__row">
                                                        <FaPhone />
                                                        <a href={`tel:${highestBid.phoneNumber}`}>
                                                            {formatPhoneNumber(highestBid.phoneNumber)}
                                                        </a>
                                                    </div>
                                                    <div className="auctiondetail-elaqe__row">
                                                        <FaWhatsapp />
                                                        <a 
                                                            href={`https://wa.me/${highestBid.phoneNumber.replace(/\D/g, '')}`} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                        >
                                                            WhatsApp
                                                        </a>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <p className="auctiondetail-elaqe__nobids">⚠️ Təklif verilmədi.</p>
                                )
                            ) : (isAdmin || isWinner) && auctionEnded ? (
                                <div className="auctiondetail-elaqe__seller-info">
                                    {isWinner && (
                                        <p className="auctiondetail-elaqe__winner-badge">🎉 Siz qalibsiniz!</p>
                                    )}
                                    <p className="auctiondetail-elaqe__info-text">Satıcı ilə əlaqə:</p>
                                    {sellerPhone ? (
                                        <div className="auctiondetail-elaqe__details">
                                            <div className="auctiondetail-elaqe__row">
                                                <FaUser />
                                                <span>{sellerName}</span>
                                            </div>
                                            <div className="auctiondetail-elaqe__row">
                                                <FaPhone />
                                                <a href={`tel:${sellerPhone}`}>
                                                    {formatPhoneNumber(sellerPhone)}
                                                </a>
                                            </div>
                                            <div className="auctiondetail-elaqe__row">
                                                <FaWhatsapp />
                                                <a 
                                                    href={`https://wa.me/${sellerPhone.replace(/\D/g, '')}`} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                >
                                                    WhatsApp
                                                </a>
                                            </div>
                                        </div>
                                    ) : (
                                        <p className="auctiondetail-elaqe__loading">Satıcı məlumatları yüklənir...</p>
                                    )}
                                </div>
                            ) : (
                                <p className="auctiondetail-elaqe__hidden">
                                    {auctionEnded ? "Yalnız qalib görə bilər." : "Hərrac bitdikdən sonra göstəriləcək."}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Extend Auction */}
                    {canExtendAuction && (
                        <div className="auctiondetail-extend">
                            <h3>⏰ Vaxtı Uzat</h3>
                            <div className="auctiondetail-extend__form">
                                <DatePicker 
                                    showTime 
                                    format="DD.MM.YYYY HH:mm" 
                                    placeholder="Yeni tarix" 
                                    value={newEndDate ? dayjs(newEndDate) : null} 
                                    onChange={(date) => setNewEndDate(date ? date.toISOString() : '')} 
                                    disabledDate={(current) => current && (current < dayjs() || current > dayjs().add(MAX_EXTEND_DAYS, 'days'))} 
                                />
                                <button 
                                    className="auctiondetail-extend__btn" 
                                    onClick={handleExtendAuction} 
                                    disabled={extendingAuction || !newEndDate}
                                >
                                    {extendingAuction ? "Gözləyin..." : "Uzat"}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Winner Actions */}
                    {canShowConfirmDeposit && (
                        <div className="auctiondetail-deposit">
                            <button 
                                className="auctiondetail-deposit__btn" 
                                onClick={() => setConfirmDepositModal(true)}
                            >
                                <FaCheckCircle />
                                <span>Məhsulu Aldım</span>
                            </button>
                        </div>
                    )}
                    
                    {isWinner && !depositAlreadyConfirmed && !hasReported && auctionEnded && (
                        <div className="auctiondetail-report">
                            <button 
                                className="auctiondetail-report__btn" 
                                onClick={() => setShowReportModal(true)}
                            >
                                <FaExclamationTriangle />
                                <span>Problem Var</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Product Details */}
            {product.subCategoryId && (
                <div className="auctiondetail-specs">
                    <DynamicProductDetails subCategoryId={product.subCategoryId} productId={product.id} />
                </div>
            )}

            {/* Image Modal */}
            {isModalOpen && (
                <div className="auctiondetail-modal" onClick={closeImageModal}>
                    <div className="auctiondetail-modal__box" onClick={(e) => e.stopPropagation()}>
                        <button className="auctiondetail-modal__close" onClick={closeImageModal}>
                            <FaTimes />
                        </button>
                        {hasImages && (
                            <>
                                <button className="auctiondetail-modal__nav auctiondetail-modal__nav--prev" onClick={() => changeImage(-1)}>
                                    <FaChevronLeft />
                                </button>
                                <img src={activeImageUrl} alt={product.name} onError={() => handleImageError(activeIndex)} />
                                <button className="auctiondetail-modal__nav auctiondetail-modal__nav--next" onClick={() => changeImage(1)}>
                                    <FaChevronRight />
                                </button>
                                <div className="auctiondetail-modal__counter">{activeIndex + 1} / {product.images.length}</div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Confirm Bid Modal */}
            {confirmBidModal && (
                <div className="auctiondetail-popup" onClick={() => !isSubmittingBid && setConfirmBidModal(false)}>
                    <div className="auctiondetail-popup__box" onClick={(e) => e.stopPropagation()}>
                        <h3>Təklifi Təsdiqlə</h3>
                        <div className="auctiondetail-popup__amount">
                            <FaGavel />
                            <span>{formatCurrency(bidToConfirm)}</span>
                        </div>
                        {!hasUserDeposit && (
                            <div className="auctiondetail-popup__deposit-warning">
                                <FaCoins />
                                <p>İlk təklif: <strong>{formatCurrency(requiredDeposit)}</strong> depozit tutulacaq</p>
                                <small>Balans: {formatCurrency(balance)}</small>
                            </div>
                        )}
                        <div className="auctiondetail-popup__btns">
                            <button 
                                className="auctiondetail-popup__btn auctiondetail-popup__btn--yes" 
                                onClick={confirmAndSubmitBid} 
                                disabled={isSubmittingBid || (!hasUserDeposit && balance < requiredDeposit)}
                            >
                                <FaCheckCircle /> {isSubmittingBid ? "Göndərilir..." : "Təsdiqlə"}
                            </button>
                            <button 
                                className="auctiondetail-popup__btn auctiondetail-popup__btn--no" 
                                onClick={() => setConfirmBidModal(false)} 
                                disabled={isSubmittingBid}
                            >
                                <FaTimes /> Ləğv et
                            </button>
                        </div>
                        {!hasUserDeposit && balance < requiredDeposit && (
                            <Link to="/kabinetim/balans" className="auctiondetail-popup__topup">💳 Balans artır</Link>
                        )}
                    </div>
                </div>
            )}

            {/* Confirm Deposit Modal */}
            {confirmDepositModal && (
                <div className="auctiondetail-popup" onClick={() => !confirmingDeposit && setConfirmDepositModal(false)}>
                    <div className="auctiondetail-popup__box" onClick={(e) => e.stopPropagation()}>
                        <h3>Depozit Təsdiqi</h3>
                        <p>Məhsulu aldığınıza əminsiniz?</p>
                        {depositInfo && (
                            <div className="auctiondetail-popup__summary">
                                <div className="auctiondetail-popup__row">
                                    <span>Depozit:</span>
                                    <strong>{formatCurrency(depositInfo.depositAmount)}</strong>
                                </div>
                                <div className="auctiondetail-popup__row">
                                    <span>Xidmət haqqı:</span>
                                    <strong>{formatCurrency(depositInfo.platformFee)}</strong>
                                </div>
                                <div className="auctiondetail-popup__row auctiondetail-popup__row--highlight">
                                    <span>Satıcıya:</span>
                                    <strong>{formatCurrency(depositInfo.sellerGets)}</strong>
                                </div>
                            </div>
                        )}
                        <div className="auctiondetail-popup__btns">
                            <button 
                                className="auctiondetail-popup__btn auctiondetail-popup__btn--yes" 
                                onClick={handleConfirmDeposit} 
                                disabled={confirmingDeposit}
                            >
                                <FaCheckCircle /> {confirmingDeposit ? "Gözləyin..." : "Təsdiqlə"}
                            </button>
                            <button 
                                className="auctiondetail-popup__btn auctiondetail-popup__btn--no" 
                                onClick={() => setConfirmDepositModal(false)} 
                                disabled={confirmingDeposit}
                            >
                                <FaTimes /> Ləğv et
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Report Modal */}
            {showReportModal && (
                <div className="auctiondetail-popup" onClick={() => !isReporting && setShowReportModal(false)}>
                    <div className="auctiondetail-popup__box" onClick={(e) => e.stopPropagation()}>
                        <h3>Şikayət</h3>
                        <textarea 
                            className="auctiondetail-popup__textarea" 
                            rows="5" 
                            placeholder="Problemi izah edin..." 
                            value={reportMessage} 
                            onChange={(e) => setReportMessage(e.target.value)} 
                        />
                        <div className="auctiondetail-popup__btns">
                            <button 
                                className="auctiondetail-popup__btn auctiondetail-popup__btn--yes" 
                                onClick={submitReport} 
                                disabled={isReporting}
                            >
                                <FaCheckCircle /> {isReporting ? "Göndərilir..." : "Göndər"}
                            </button>
                            <button 
                                className="auctiondetail-popup__btn auctiondetail-popup__btn--no" 
                                onClick={() => setShowReportModal(false)}
                            >
                                <FaTimes /> Ləğv et
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Deposit Info Modal */}
            {showDepositInfo && (
                <div className="auctiondetail-popup" onClick={() => setShowDepositInfo(false)}>
                    <div className="auctiondetail-popup__box auctiondetail-popup__box--wide" onClick={(e) => e.stopPropagation()}>
                        <div className="auctiondetail-popup__header">
                            <h3><FaCoins /> Pilləli Depozit Sistemi</h3>
                            <button className="auctiondetail-popup__close" onClick={() => setShowDepositInfo(false)}>
                                <FaTimes />
                            </button>
                        </div>
                        <div className="auctiondetail-popup__body">
                            <div className="auctiondetail-popup__table">
                                <table>
                                    <thead><tr><th>Qiymət</th><th>Depozit</th></tr></thead>
                                    <tbody>
                                        <tr><td>0 - 100 AZN</td><td>5 AZN</td></tr>
                                        <tr><td>100 - 500 AZN</td><td>10 AZN</td></tr>
                                        <tr><td>500 - 1,000 AZN</td><td>20 AZN</td></tr>
                                        <tr><td>1,000 - 2,500 AZN</td><td>40 AZN</td></tr>
                                        <tr><td>2,500 - 5,000 AZN</td><td>75 AZN</td></tr>
                                        <tr><td>5,000 - 10,000 AZN</td><td>125 AZN</td></tr>
                                        <tr><td>10,000 - 25,000 AZN</td><td>250 AZN</td></tr>
                                        <tr><td>25,000 - 50,000 AZN</td><td>400 AZN</td></tr>
                                        <tr><td>50,000 - 100,000 AZN</td><td>650 AZN</td></tr>
                                        <tr><td>100,000+ AZN</td><td>1,000 AZN</td></tr>
                                    </tbody>
                                </table>
                            </div>
                            <div className="auctiondetail-popup__info">
                                <ul>
                                    <li>Depozit yalnız ilk təklifdə tutulur</li>
                                    <li>Qalib olmadıqda tam qaytarılır</li>
                                    <li>Platforma haqqı: {DEPOSIT_CONFIG.PLATFORM_FEE_PERCENTAGE * 100}%</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-center" autoClose={4000} hideProgressBar={false} newestOnTop closeOnClick pauseOnHover theme="light" />
        </div>
    );
}

export default AnnouncementItem;